import compact from "lodash/compact";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import pick from "lodash/pick";
import {
  FIELD_CONVERTED_AVAILABILITY_KEY,
  FIELD_CUSTOM_FIELDS_KEY,
  FIELD_INTEREST_QUESTIONS_KEY,
  FIELD_INTEREST_QUESTIONS_TOGGLE_KEY,
  FIELD_METADATA_KEY,
  FIELD_PARENT_ACTIVITY_ID_KEY,
  FIELD_PARENT_ACTIVITY_KEY,
  FIELD_SUBTEMPLATE_KEY,
  NO_SUBTEMPLATE_VALUE
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { sanitizeFormDataForAPI } from "PFApp/helpers/activity_form/data_conversions";
import { CustomFieldToSave } from "PFApp/hooks/activity_form/use_custom_fields_to_save";
import {
  UnsavedEdit,
  UnsavedEdits,
  UnsavedEditStatus
} from "PFApp/stores/manage_roles/manage_roles_store.types";
import { ManageRolesFormType } from "PFApp/types/activity_form/manage_roles_form_schema";
import { ActivityData, AvailabilityMode, Id, Profile } from "PFCore/types";

import { isEmptyValue } from "./use_form_unsaved_edits_tracker.utils";

type RoleEditGroup = { keys: string[]; edits: UnsavedEdit[] };

type GetCustomFieldsToSave = (
  customFields: CustomFieldToSave[],
  activityCreator: Profile | null
) => CustomFieldToSave[];

type SanitizedPayload = ReturnType<typeof sanitizeFormDataForAPI>;

type CustomFieldEntry = { type: { name: string }; values: unknown[] };

const getSubFieldName = (fieldPath: string, parent: string): string | null => {
  const prefix = `${parent}.`;

  if (fieldPath.startsWith(prefix)) {
    return fieldPath.slice(prefix.length);
  }

  return null;
};

const buildFilteredPayload = (
  full: SanitizedPayload,
  fieldNames: string[],
  rawValues: ManageRolesFormType,
  withAllData?: boolean
): Record<string, unknown> => {
  const result: Record<string, unknown> = withAllData ? { ...full } : { id: full.id };

  const metadataSubKeys: string[] = [];
  const customFieldSubKeys = new Set<string>();
  const topLevelKeys: string[] = [];
  let convertedAvailabilityOverride: Record<string, unknown> | null = null;

  for (const fieldName of fieldNames) {
    if (fieldName === FIELD_CONVERTED_AVAILABILITY_KEY) {
      const mode = rawValues.metadata?.availability?.mode;
      const { convertedAvailability } = rawValues;

      if (mode === AvailabilityMode.PerWeek) {
        convertedAvailabilityOverride = {
          availability: {
            ...(full?.metadata?.availability ?? {}),
            ranges: convertedAvailability.ranges
          }
        };
      }
      continue;
    }

    const metadataKey = getSubFieldName(fieldName, FIELD_METADATA_KEY);
    const customFieldKey = getSubFieldName(fieldName, FIELD_CUSTOM_FIELDS_KEY);

    if (metadataKey) {
      metadataSubKeys.push(metadataKey);
    } else if (customFieldKey) {
      customFieldSubKeys.add(customFieldKey);
    } else if (fieldName !== "id") {
      const name = fieldName === FIELD_PARENT_ACTIVITY_KEY ? FIELD_PARENT_ACTIVITY_ID_KEY : fieldName;
      topLevelKeys.push(name);
    }
  }

  if (metadataSubKeys.length > 0 && !withAllData) {
    result.metadata = pick(full.metadata as Record<string, unknown>, metadataSubKeys);
  }

  if (convertedAvailabilityOverride) {
    result.metadata = {
      ...((result.metadata as Record<string, unknown>) ?? {}),
      ...convertedAvailabilityOverride
    };
  }

  if (customFieldSubKeys.size > 0 && !withAllData) {
    result.customFields = (full.custom_fields as CustomFieldEntry[]).filter((entry) =>
      customFieldSubKeys.has(entry.type.name)
    );
  }

  if (topLevelKeys.length > 0) {
    Object.assign(result, pick(full as Record<string, unknown>, topLevelKeys));
  }

  return result;
};

export const groupEditsByRole = (edits: Record<string, UnsavedEdit>): Map<Id, RoleEditGroup> =>
  Object.entries(edits).reduce((byRole, [key, edit]) => {
    const group = byRole.get(edit.roleId) ?? { keys: [], edits: [] };

    group.keys.push(key);
    group.edits.push(edit);

    return byRole.set(edit.roleId, group);
  }, new Map<Id, RoleEditGroup>());

export const buildRolePayload = (roleValues: ManageRolesFormType, edits: UnsavedEdit[]) => {
  const full = sanitizeFormDataForAPI({
    values: { ...roleValues, [FIELD_PARENT_ACTIVITY_ID_KEY]: roleValues[FIELD_PARENT_ACTIVITY_KEY]?.id },
    isNew: false
  });
  const changedFieldNames = edits.map((edit) => edit.fieldName);

  return buildFilteredPayload(full, changedFieldNames, roleValues);
};

export const buildCreatePayload = (
  roleValues: ManageRolesFormType,
  getCustomFieldsToSave: GetCustomFieldsToSave,
  activityCreator: Profile | null,
  edits: UnsavedEdit[]
): ActivityData => {
  const editedFieldNames = edits.map((edit) => edit.fieldName);

  const sanitized = sanitizeFormDataForAPI({
    values: {
      ...roleValues,
      [FIELD_INTEREST_QUESTIONS_TOGGLE_KEY]: !!roleValues[FIELD_INTEREST_QUESTIONS_KEY],
      [FIELD_PARENT_ACTIVITY_ID_KEY]: roleValues[FIELD_PARENT_ACTIVITY_KEY]?.id,
      [FIELD_METADATA_KEY]: {
        ...roleValues[FIELD_METADATA_KEY],
        availability: {
          ...roleValues.metadata?.availability,
          ranges: !isEmpty(roleValues.convertedAvailability?.ranges)
            ? roleValues.convertedAvailability.ranges
            : roleValues.metadata?.availability?.ranges
        } as ManageRolesFormType["metadata"]["availability"]
      }
    },
    isNew: true,
    fieldsBlacklist: compact([
      roleValues[FIELD_SUBTEMPLATE_KEY] === NO_SUBTEMPLATE_VALUE ? FIELD_SUBTEMPLATE_KEY : null
    ])
  });

  const full = {
    ...sanitized,
    custom_fields: getCustomFieldsToSave(
      (sanitized.custom_fields as CustomFieldToSave[]) || [],
      activityCreator
    )
  };

  return buildFilteredPayload(full, editedFieldNames, roleValues, true);
};

export const getUnsavedEditsWithStatus = (unsavedEdits: UnsavedEdits, status: UnsavedEditStatus) =>
  Object.fromEntries(Object.entries(unsavedEdits).filter(([, edit]) => edit.status === status));

export const getErrorLeafFieldName = (fieldName: string): string => {
  const segments = fieldName.split(/[[\].]/).filter(Boolean);
  return segments[segments.length - 1];
};

export const getFormErrorFieldNames = (errors: Record<string, unknown>): string[] =>
  Array.from(
    new Set(
      Object.keys(errors).map((errorField) => {
        const leafFieldName = getErrorLeafFieldName(errorField);
        return leafFieldName === FIELD_PARENT_ACTIVITY_ID_KEY ? FIELD_PARENT_ACTIVITY_KEY : leafFieldName;
      })
    )
  );

export const resolveErrorFieldValue = (roleValues: ManageRolesFormType, fieldName: string) =>
  get(roleValues, `${FIELD_CUSTOM_FIELDS_KEY}.${fieldName}`) ??
  get(roleValues, `${FIELD_METADATA_KEY}.${fieldName}`) ??
  get(roleValues, fieldName);

type MatchedErrorFieldNames = {
  matchedKeys: string[];
  unmatchedFieldNames: string[];
};

export const matchErrorFieldNamesToKeys = (
  errorFieldNames: string[],
  keys: string[]
): MatchedErrorFieldNames => {
  const matchedKeys: string[] = [];
  const unmatchedFieldNames: string[] = [];

  errorFieldNames.forEach((fieldName) => {
    const matchingKey = keys.find((key) => key.endsWith(`.${fieldName}`));
    if (matchingKey) {
      matchedKeys.push(matchingKey);
    } else {
      unmatchedFieldNames.push(fieldName);
    }
  });

  return { matchedKeys, unmatchedFieldNames };
};

export const getEmptyErroredEdits = (
  errorFieldNames: string[],
  edits: UnsavedEdit[],
  roleValues: ManageRolesFormType
): UnsavedEdit[] =>
  edits.filter(
    (edit) =>
      errorFieldNames.includes(getErrorLeafFieldName(edit.fieldName)) &&
      isEmptyValue(get(roleValues, edit.fieldName)) &&
      !isEmptyValue(edit.previousValue)
  );
